import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { useSSRData } from "../contexts/SSRDataContext";
import { slugify } from "@shared/slug";
import { buildSeoTitle } from "@shared/seoTitle";

interface DrugSummary {
  genericName: string;
  slug: string;
  productCount: number;
  anyInStock: boolean;
  brandNames?: string[];
}

interface RelatedDrug {
  genericName: string;
  slug: string;
  productCount: number;
  anyInStock: boolean;
}
interface DrugDetail {
  genericName: string;

  slug: string;

  productCount: number;

  inStockCount: number;

  strengths: string[];

  dosageForms: string[];

  manufacturers: string[];

  ndcs: string[];

  anyInStock: boolean;

  pharmClasses?: Array<{ className: string; slug: string }>;

  education: {
    indicationsAndUsage: string | null;
    warnings: string | null;
    adverseReactions: string | null;
    drugInteractions: string | null;
    dosageAndAdministration: string | null;
    boxedWarning: string | null;
    mechanismOfAction: string | null;
    clinicalPharmacology: string | null;
    contraindicationsAndPrecautions: string | null;
    warningsAndPrecautions: string | null;
    informationForPatients: string | null;

  } | null;

  clinicalIds: {
    rxcuis: string[];
    teCodes: string[];
    deaSchedule: string | null;
    pharmClass: string | null;
    storageRequirements: string | null;
    medlineplusUrl: string | null;
  };

  products: Array<{
    id: number;
    ndc: string;
    name: string;
    strength: string | null;
    dosageForm: string | null;
    manufacturer: string | null;
    inStock: boolean | null;
    imageUrl: string | null;

  }>;

  brandNames: string[];

  relatedDrugs?: RelatedDrug[];
}

function truncate(text: string | null | undefined, maxLen: number): string {
  if (!text) return "";
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).trimEnd() + "…";
}

/** Plain-English label for a TE code prefix. */
function teCodeLabel(code: string): string {
  const prefix = code.slice(0, 2).toUpperCase();
  const map: Record<string, string> = {
    AB: "Therapeutically equivalent — meets FDA bioequivalence requirements",
    AA: "Therapeutically equivalent — conventional dosage form, no bioequivalence issues",
    AN: "Therapeutically equivalent — aerosolization solution/powder",
    AO: "Therapeutically equivalent — injectable oil solution",
    AP: "Therapeutically equivalent — injectable aqueous solution",
    AT: "Therapeutically equivalent — topical product",
    BC: "Not equivalent — extended-release form, bioequivalence not established",
    BD: "Not equivalent — documented bioequivalence problem",
    BE: "Not equivalent — delayed-release oral form",
    BN: "Not equivalent — aerosol-nebulizer product",
    BP: "Not equivalent — potential bioequivalence problem",
    BR: "Not equivalent — suppository or enema",
    BS: "Not equivalent — drug standard deficiency",
    BT: "Not equivalent — topical product with bioequivalence issues",
    BX: "Not equivalent — insufficient data",
  };
  return map[prefix] ?? `TE code ${code}`;
}
function DrugIndexPage({ drugs }: { drugs: DrugSummary[] }) {
  const [search, setSearch] = useState("");
  const filtered = search.trim()
    ? drugs.filter((d) => {
        const q = search.toLowerCase();
        if (d.genericName.toLowerCase().includes(q)) return true;
        if (d.brandNames?.some((b) => b.toLowerCase().includes(q))) return true;
        return false;
      })
    : drugs;

  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const byLetter = new Map<string, DrugSummary[]>();
  for (const drug of filtered) {
    const letter = drug.genericName[0].toUpperCase();
    if (!byLetter.has(letter)) byLetter.set(letter, []);
    byLetter.get(letter)!.push(drug);
  }

  const collectionJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Generic Drug Wholesale Directory",
    description: `Browse ${drugs.length}+ generic drugs available for wholesale purchase from Premier Pharma. Licensed pharmacies, clinics, doctor's offices, and veterinary practices can view pricing and place orders same-day.`,
    url: "https://premierpharma.com/drugs",
    numberOfItems: drugs.length,
    isPartOf: { "@id": "https://premierpharma.com/#website" },
  };

  return (
    <>
      <SEO
        title={buildSeoTitle("Generic Drug Wholesale Directory — All Drugs")}
        description={`Browse ${drugs.length}+ generic drugs available wholesale at Premier Pharma. Pharmacies, clinics, and veterinary practices — view pricing and order same-day.`}
        path="/drugs"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Drug Directory", path: "/drugs" },
        ]}
        jsonLd={[collectionJsonLd]}
      />

      <section className="bg-gradient-to-br from-primary via-[#0a1e4a] to-[#0d2d6b] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-xs text-white/70 mb-3">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white">Drug Directory</span>
          </nav>
          <h1 className="text-3xl lg:text-4xl font-bold mb-3">Generic Drug Wholesale Directory</h1>
          <p className="text-white/80 text-sm sm:text-base mb-6">
            {drugs.length.toLocaleString()} generic drugs · Wholesale pricing for licensed healthcare providers · DSCSA-compliant
          </p>
          <input
            type="search"
            aria-label="Search drugs by generic or brand name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by generic or brand name…"
            className="w-full max-w-md bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-white/50 text-sm focus:outline-none focus:bg-white/20 focus:ring-2 focus:ring-white/60"
          />
        </div>
      </section>

      <section className="py-10 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {search.trim() ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filtered.map((drug) => {
                const matchedBrands = search.trim()
                  ? (drug.brandNames || []).filter((b) => b.toLowerCase().includes(search.toLowerCase()))
                  : [];
                return (
                  <Link
                    key={drug.slug}
                    to={`/drugs/${drug.slug}`}
                    className="bg-white border border-gray-200 rounded-lg p-3 hover:border-accent/40 hover:shadow-sm transition-all"
                  >
                    <div className="font-medium text-primary text-sm leading-tight">
                      {drug.genericName.charAt(0).toUpperCase() + drug.genericName.slice(1).toLowerCase()}
                    </div>
                    {matchedBrands.length > 0 && (
                      <div className="text-xs text-gray-500 mt-0.5 italic">
                        brand: {matchedBrands.slice(0, 2).join(", ")}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">{drug.productCount} SKUs</div>
                    {drug.anyInStock && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-700 mt-1">
                        <span className="w-1 h-1 rounded-full bg-green-500" />
                        In Stock
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="space-y-8">
              {letters.map((letter) => {
                const group = byLetter.get(letter);
                if (!group || group.length === 0) return null;
                return (
                  <div key={letter}>
                    <h2 className="text-lg font-bold text-primary border-b border-gray-200 pb-1 mb-3">{letter}</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {group.map((drug) => (
                        <Link
                          key={drug.slug}
                          to={`/drugs/${drug.slug}`}
                          className="bg-white border border-gray-200 rounded-lg p-3 hover:border-accent/40 hover:shadow-sm transition-all"
                        >
                          <div className="font-medium text-primary text-sm leading-tight">
                            {drug.genericName.charAt(0).toUpperCase() + drug.genericName.slice(1).toLowerCase()}
                          </div>
                          {drug.brandNames && drug.brandNames.length > 0 && (
                            <div className="text-[10px] text-gray-400 mt-0.5 truncate">
                              {drug.brandNames.slice(0, 2).join(", ")}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">{drug.productCount} SKUs</div>
                          {drug.anyInStock && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-700 mt-1">
                              <span className="w-1 h-1 rounded-full bg-green-500" />
                              In Stock
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              <p className="text-lg font-medium mb-2">No drugs found for "{search}"</p>
              <p className="text-sm">Try a broader search or <Link to="/products" className="text-accent hover:underline">browse the full catalog</Link>.</p>
            </div>
          )}

          <div className="mt-12 bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 text-center">
            <h2 className="text-xl font-bold text-primary mb-2">Ready to order at wholesale?</h2>
            <p className="text-gray-500 text-sm mb-5 max-w-2xl mx-auto">
              Create a free Premier Pharma account to unlock live wholesale pricing, place orders, and track shipments — approval typically takes minutes for licensed pharmacies, clinics, doctor's offices, and veterinary practices.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/register" className="bg-accent text-white px-5 py-2.5 rounded-lg font-medium hover:bg-accent/90">Create Account</Link>
              <Link to="/products" className="bg-white border border-gray-300 text-primary px-5 py-2.5 rounded-lg font-medium hover:bg-gray-50">Browse Full Catalog</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function DrugDetailPage({ drug }: { drug: DrugDetail }) {
  const displayName = drug.genericName.charAt(0).toUpperCase() + drug.genericName.slice(1).toLowerCase();
  const primaryBrand = drug.brandNames && drug.brandNames.length > 0 ? drug.brandNames[0] : null;

  const strengthStr = drug.strengths.length > 0
    ? drug.strengths.slice(0, 3).join(", ")
    : "";
  const firstStrength = drug.strengths.length > 0 ? drug.strengths[0] : "";
  const formStr = drug.dosageForms.length > 0 ? drug.dosageForms[0] : "";

  const brandSuffix = primaryBrand ? ` (generic ${primaryBrand})` : "";
  const title = buildSeoTitle(`${displayName}${brandSuffix}${firstStrength ? ` ${firstStrength}` : ""}${formStr ? ` ${formStr}` : ""} Wholesale`);
  const description = primaryBrand
    ? `Buy wholesale ${displayName} (generic for ${primaryBrand}) from Premier Pharma. ${drug.productCount} SKUs available${strengthStr ? ` in ${strengthStr}` : ""}${formStr ? ` ${formStr}` : ""}. DSCSA-compliant, same-day shipping for licensed pharmacies, clinics, and veterinary practices.`
    : `Buy wholesale ${displayName} from Premier Pharma. ${drug.productCount} SKUs available${strengthStr ? ` in ${strengthStr}` : ""}${formStr ? ` ${formStr}` : ""}. DSCSA-compliant, same-day shipping for licensed pharmacies, clinics, and veterinary practices.`;

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Drug Directory", path: "/drugs" },
    { name: displayName, path: `/drugs/${drug.slug}` },
  ];

  // ── Drug structured data (schema.org/Drug) ─────────────────────────────
  // Mirrors the server-side Drug JSON-LD built in server/drugPages.ts so
  // the same markup is present after React hydration and during SPA nav.
  const drugSchemaJsonLd: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Drug",
    name: displayName,
    nonProprietaryName: drug.genericName,
    url: `https://premierpharma.com/drugs/${drug.slug}`,
    description,
  };
  if (drug.brandNames.length > 0) drugSchemaJsonLd.alternateName = drug.brandNames;
  if (drug.dosageForms.length > 0) drugSchemaJsonLd.dosageForm = drug.dosageForms[0];
  const ids = drug.clinicalIds;
  if (ids.rxcuis.length > 0) drugSchemaJsonLd.rxcui = ids.rxcuis[0];
  if (ids.pharmClass) drugSchemaJsonLd.drugClass = ids.pharmClass;
  if (ids.medlineplusUrl) drugSchemaJsonLd.sameAs = ids.medlineplusUrl;
  if (ids.deaSchedule) {
    drugSchemaJsonLd.legalStatus = `DEA Schedule ${ids.deaSchedule}`;
    drugSchemaJsonLd.prescriptionStatus = "https://schema.org/PrescriptionOnly";
  }
  drugSchemaJsonLd.isProprietary = false;
  drugSchemaJsonLd.offers = {
    "@type": "AggregateOffer",
    offerCount: drug.productCount,
    priceCurrency: "USD",
    availability: drug.anyInStock
      ? "https://schema.org/InStock"
      : "https://schema.org/PreOrder",
    seller: { "@id": "https://premierpharma.com/#organization" },
  };

  const edu = drug.education;
  const hasIdentifiers = ids.rxcuis.length > 0 || ids.teCodes.length > 0 || ids.deaSchedule || ids.pharmClass || ids.storageRequirements;

  // ── FAQPage structured data (schema.org/FAQPage) ───────────────────────
  // Mirrors the server-side FAQ JSON-LD built in server/drugPages.ts so the
  // same markup is present after React hydration and during SPA navigations.
  function teCodeExplainer(code: string): string {
    const prefix = code.slice(0, 2).toUpperCase();
    const explanations: Record<string, string> = {
      AA: "Therapeutically equivalent — conventional dosage form with no known bioequivalence issues.",
      AB: "Therapeutically equivalent — meets FDA bioequivalence requirements; may be substituted for the reference listed drug.",
      AN: "Therapeutically equivalent — aerosolization solution or powder.",
      AO: "Therapeutically equivalent — injectable oil solution.",
      AP: "Therapeutically equivalent — injectable aqueous solution.",
      AT: "Therapeutically equivalent — topical product.",
      BC: "Not therapeutically equivalent — extended-release form that does not meet bioequivalence standards.",
      BD: "Not therapeutically equivalent — documented bioequivalence problem with this active ingredient and dosage form.",
      BE: "Not therapeutically equivalent — delayed-release oral dosage form.",
      BN: "Not therapeutically equivalent — aerosol-nebulizer product.",
      BP: "Not therapeutically equivalent — potential bioequivalence problem.",
      BR: "Not therapeutically equivalent — suppository or enema.",
      BS: "Not therapeutically equivalent — drug standard deficiency.",
      BT: "Not therapeutically equivalent — topical product with documented bioequivalence issues.",
      BX: "Not therapeutically equivalent — insufficient data to determine equivalence.",
    };
    return explanations[prefix] ?? `TE code ${code} — consult the FDA Orange Book for details.`;
  }

  const faqEntities: Record<string, any>[] = [
    {
      "@type": "Question",
      name: `Where can I buy wholesale ${displayName}${primaryBrand ? ` (${primaryBrand})` : ""} for my pharmacy?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `Premier Pharma stocks ${drug.productCount} ${displayName} SKUs — ${drug.inStockCount} currently in stock — available to licensed pharmacies, clinics, doctor's offices, and veterinary practices.${primaryBrand ? ` ${displayName} is the generic equivalent of ${primaryBrand}.` : ""} Create a free account to see live wholesale pricing and place orders with same-day fulfillment for in-stock items.`,
      },
    },
    {
      "@type": "Question",
      name: `What strengths and dosage forms of ${displayName} are available?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `Premier Pharma carries ${displayName} in ${drug.strengths.length > 0 ? drug.strengths.slice(0, 5).join(", ") : "multiple strengths"} and ${drug.dosageForms.length > 0 ? drug.dosageForms.join(", ") : "various dosage forms"}. ${drug.manufacturers.length > 0 ? `Available from manufacturers including ${drug.manufacturers.slice(0, 3).join(", ")}.` : ""}`,
      },
    },
  ];

  if (ids.teCodes.length > 0) {
    const teCode = ids.teCodes[0];
    faqEntities.push({
      "@type": "Question",
      name: `What is the therapeutic equivalence (TE) rating for ${displayName}?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${displayName} carries an FDA Orange Book TE code of ${teCode}. ${teCodeExplainer(teCode)} Always verify substitution with the current Orange Book before dispensing.`,
      },
    });
  }

  if (ids.deaSchedule) {
    faqEntities.push({
      "@type": "Question",
      name: `Is ${displayName} a controlled substance?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${displayName} is classified as DEA Schedule ${ids.deaSchedule}. Ordering and dispensing requirements vary by state; ensure your pharmacy's DEA registration covers this schedule.`,
      },
    });
  }

  if (edu?.mechanismOfAction) {
    faqEntities.push({
      "@type": "Question",
      name: `How does ${displayName} work?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: edu.mechanismOfAction.slice(0, 600),
      },
    });
  }

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqEntities,
  };

  return (
    <>
      <SEO
        title={title}
        description={description}
        path={`/drugs/${drug.slug}`}
        breadcrumbs={breadcrumbs}
        jsonLd={[drugSchemaJsonLd, faqJsonLd]}
      />

      <section className="bg-gradient-to-br from-primary via-[#0a1e4a] to-[#0d2d6b] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-xs text-white/70 mb-3">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/drugs" className="hover:text-white">Drug Directory</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{displayName}</span>
          </nav>
          <h1 className="text-3xl lg:text-4xl font-bold leading-tight mb-2">
            {displayName}
            {primaryBrand && (
              <span className="font-normal text-white/70 text-2xl lg:text-3xl"> (generic {primaryBrand})</span>
            )}
            {" "}Wholesale
          </h1>
          {drug.brandNames && drug.brandNames.length > 0 && (
            <p className="text-white/60 text-sm mb-1">
              Generic equivalent of {drug.brandNames.join(", ")}
            </p>
          )}
          <p className="text-white/80 text-sm sm:text-base">
            {drug.productCount} SKUs available · {drug.anyInStock ? "In stock" : "Available to order"} · NABP accredited · DSCSA compliant
          </p>
        </div>
      </section>

      <section className="py-10 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">

          {/* Intro copy connecting generic to brand */}
          {primaryBrand && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong>{displayName}</strong> is the generic equivalent of the brand-name drug{" "}
                <strong>{drug.brandNames.join(drug.brandNames.length > 1 ? ", " : "")}</strong>.
                {" "}Licensed pharmacies, clinics, doctor's offices, and veterinary practices can source wholesale {displayName} through Premier Pharma with same-day fulfillment on in-stock SKUs.
              </p>
            </div>
          )}

          {/* Boxed Warning — visually prominent */}
          {edu?.boxedWarning && (
            <div className="border-2 border-red-600 rounded-xl p-5 bg-red-50">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-red-600 text-xl font-black leading-none">⚠</span>
                <h2 className="text-base font-bold text-red-700 uppercase tracking-wide">FDA Boxed Warning</h2>
              </div>
              <p className="text-red-800 text-sm leading-relaxed whitespace-pre-line">{truncate(edu.boxedWarning, 800)}</p>
              <p className="text-xs text-red-500 mt-3">Source: FDA prescribing information. For licensed professionals only.</p>
            </div>
          )}

          {/* Strengths / Forms / Manufacturers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {drug.strengths.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Available Strengths</h2>
                <div className="flex flex-wrap gap-2">
                  {drug.strengths.map((s) => (
                    <span key={s} className="bg-blue-50 text-accent text-sm px-2.5 py-1 rounded-full">{s}</span>
                  ))}
                </div>
              </div>
            )}
            {drug.dosageForms.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Dosage Forms</h2>
                <div className="flex flex-wrap gap-2">
                  {drug.dosageForms.map((f) => (
                    <span key={f} className="bg-gray-100 text-gray-700 text-sm px-2.5 py-1 rounded-full">{f}</span>
                  ))}
                </div>
              </div>
            )}
            {drug.manufacturers.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Manufacturers</h2>
                <ul className="space-y-1">
                  {drug.manufacturers.slice(0, 6).map((m) => (
                    <li key={m} className="text-sm">
                      <Link to={`/manufacturers/${slugify(m)}`} className="text-gray-700 hover:text-accent hover:underline">{m}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Therapeutic Class links */}
          {drug.pharmClasses && drug.pharmClasses.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Therapeutic Class</h2>
              <div className="flex flex-wrap gap-2">
                {drug.pharmClasses.map((cls) => (
                  <Link
                    key={cls.slug}
                    to={`/therapeutic-classes/${cls.slug}`}
                    className="bg-blue-50 text-accent text-sm px-3 py-1 rounded-full hover:bg-accent hover:text-white transition-colors"
                  >
                    {cls.className}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Clinical Identifiers Panel */}
          {hasIdentifiers && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h2 className="text-xl font-bold text-primary mb-4">Clinical Identifiers</h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {ids.rxcuis.length > 0 && (
                  <div>
                    <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">RxCUI</dt>
                    <dd className="flex flex-wrap gap-1.5">
                      {ids.rxcuis.map((r) => (
                        <span key={r} className="font-mono text-sm bg-gray-100 text-gray-800 px-2 py-0.5 rounded">{r}</span>
                      ))}
                    </dd>
                  </div>
                )}
                {ids.teCodes.length > 0 && (
                  <div className="sm:col-span-2 lg:col-span-2">
                    <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Therapeutic Equivalence (TE) Code</dt>
                    <dd className="space-y-1.5">
                      {ids.teCodes.map((te) => (
                        <div key={te} className="flex items-start gap-2">
                          <span className="font-mono text-sm font-bold bg-blue-50 text-accent px-2 py-0.5 rounded shrink-0">{te}</span>
                          <span className="text-sm text-gray-600 leading-snug">{teCodeLabel(te)}</span>
                        </div>
                      ))}
                    </dd>
                  </div>
                )}
                {ids.deaSchedule && (
                  <div>
                    <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">DEA Schedule</dt>
                    <dd className="text-sm text-gray-800 font-medium">Schedule {ids.deaSchedule}</dd>
                  </div>
                )}
                {ids.pharmClass && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Pharmacologic Class</dt>
                    <dd className="text-sm text-gray-700">{ids.pharmClass}</dd>
                  </div>
                )}
                {ids.storageRequirements && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Storage Requirements</dt>
                    <dd className="text-sm text-gray-700">{ids.storageRequirements}</dd>
                  </div>
                )}
              </dl>
              {ids.medlineplusUrl && (
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <a
                    href={ids.medlineplusUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                    MedlinePlus patient information for {displayName} ↗
                  </a>
                </div>
              )}
            </div>
          )}

          {/* FDA Drug Information */}
          {edu && (edu.indicationsAndUsage || edu.warnings || edu.adverseReactions || edu.drugInteractions || edu.dosageAndAdministration || edu.mechanismOfAction || edu.clinicalPharmacology || edu.contraindicationsAndPrecautions || edu.warningsAndPrecautions || edu.informationForPatients) && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
              <h2 className="text-xl font-bold text-primary">FDA Drug Information</h2>

              {edu.indicationsAndUsage && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Indications &amp; Usage</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{truncate(edu.indicationsAndUsage, 700)}</p>
                </div>
              )}

              {edu.mechanismOfAction && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Mechanism of Action</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{truncate(edu.mechanismOfAction, 700)}</p>
                </div>
              )}

              {edu.clinicalPharmacology && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Clinical Pharmacology</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{truncate(edu.clinicalPharmacology, 700)}</p>
                </div>
              )}

              {edu.contraindicationsAndPrecautions && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Contraindications &amp; Precautions</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{truncate(edu.contraindicationsAndPrecautions, 600)}</p>
                </div>
              )}

              {edu.warningsAndPrecautions && !edu.contraindicationsAndPrecautions && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Warnings &amp; Precautions</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{truncate(edu.warningsAndPrecautions, 600)}</p>
                </div>
              )}

              {edu.warnings && !edu.warningsAndPrecautions && !edu.contraindicationsAndPrecautions && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Important Warnings</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{truncate(edu.warnings, 500)}</p>
                </div>
              )}

              {edu.adverseReactions && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Adverse Reactions</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{truncate(edu.adverseReactions, 500)}</p>
                </div>
              )}

              {edu.drugInteractions && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Drug Interactions</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{truncate(edu.drugInteractions, 500)}</p>
                </div>
              )}

              {edu.dosageAndAdministration && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Dosage &amp; Administration</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{truncate(edu.dosageAndAdministration, 500)}</p>
                </div>
              )}

              {edu.informationForPatients && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Patient Counseling Information</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{truncate(edu.informationForPatients, 600)}</p>
                </div>
              )}

              <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">Source: FDA. For professional use only. Not a substitute for prescribing information.</p>
            </div>
          )}

          {/* SKU Table */}
          <div>
            <div className="flex items-end justify-between mb-4">
              <h2 className="text-xl font-bold text-primary">{displayName} SKUs Available</h2>
              <Link to="/products" className="text-sm text-accent hover:underline">Browse full catalog →</Link>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">NDC</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Strength</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Form</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {drug.products.slice(0, 30).map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/products/${p.id}`} className="text-primary hover:text-accent font-medium leading-tight text-sm line-clamp-2">
                          {p.name}
                        </Link>
                        {p.manufacturer && <div className="text-xs text-gray-400 mt-0.5">{p.manufacturer}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs font-mono hidden sm:table-cell">{p.ndc}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs hidden md:table-cell">{p.strength || "—"}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs hidden md:table-cell">{p.dosageForm || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${p.inStock ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-600"}`}>
                          <span className={`w-1 h-1 rounded-full ${p.inStock ? "bg-green-500" : "bg-blue-400"}`} />
                          {p.inStock ? "In Stock" : "2-Day"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {drug.ndcs.length > 0 && (
              <div className="mt-4 bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">NDC Codes</h3>
                <div className="flex flex-wrap gap-2">
                  {drug.ndcs.map((ndc) => (
                    <Link
                      key={ndc}
                      to={`/ndc/${encodeURIComponent(ndc)}`}
                      className="font-mono text-xs bg-gray-100 hover:bg-accent hover:text-white text-gray-700 px-2.5 py-1 rounded transition-colors"
                    >
                      {ndc}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {drug.relatedDrugs && drug.relatedDrugs.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Related Drugs</h2>
              <div className="flex flex-wrap gap-2">
                {drug.relatedDrugs.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/drugs/${rel.slug}`}
                    className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 hover:border-accent/40 hover:bg-blue-50 rounded-full px-3 py-1 text-sm text-primary hover:text-accent transition-colors"
                  >
                    {rel.genericName.charAt(0).toUpperCase() + rel.genericName.slice(1).toLowerCase()}
                    {rel.anyInStock && (
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 text-center">
            <h2 className="text-xl font-bold text-primary mb-2">Ready to order {displayName} at wholesale?</h2>
            <p className="text-gray-500 text-sm mb-5 max-w-2xl mx-auto">
              Create a free Premier Pharma account to unlock live wholesale pricing, place orders, and track shipments — approval typically takes minutes for licensed pharmacies, clinics, doctor's offices, and veterinary practices.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/register" className="bg-accent text-white px-5 py-2.5 rounded-lg font-medium hover:bg-accent/90">Create Account</Link>
              <Link to="/products" className="bg-white border border-gray-300 text-primary px-5 py-2.5 rounded-lg font-medium hover:bg-gray-50">Browse Full Catalog</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function DrugNameLanding() {
  const params = useParams<{ slug?: string }>();
  const slug = params.slug;
  const navigate = useNavigate();
  const { seoData } = useSSRData();

  const [data, setData] = useState<{ type: string; drugs?: DrugSummary[]; drug?: DrugDetail } | null>(
    seoData?.type === "drug-index" || seoData?.type === "drug-detail" ? seoData : null
  );
  const [loading, setLoading] = useState(!data);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (data && ((!slug && data.type === "drug-index") || (slug && data.type === "drug-detail" && data.drug?.slug === slug))) {
      return;
    }
    setLoading(true);
    setNotFound(false);
    const url = slug ? `/api/seo/drugs/${encodeURIComponent(slug)}` : "/api/seo/drugs";
    fetch(url)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        if (!r.ok) throw new Error("Failed");
        return r.json();
      })
      .then((d) => {
        if (!d) { setLoading(false); return; }
        // Brand-alias redirect: navigate client-side to the canonical page
        if (d.type === "drug-redirect" && d.canonicalSlug) {
          navigate(`/drugs/${d.canonicalSlug}`, { replace: true });
          setLoading(false);
          return;
        }
        setData(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  }

  if (notFound || !data) {
    return (
      <>
        <SEO title="Drug Not Found" noindex />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-2xl font-bold text-primary mb-2">Drug not found</h1>
          <p className="text-gray-500 mb-4">Browse our directory or search the full catalog.</p>
          <Link to="/drugs" className="bg-accent text-white px-5 py-2 rounded-lg font-medium hover:bg-accent/90">Drug Directory</Link>
        </div>
      </>
    );
  }

  if (data.type === "drug-index" && data.drugs) {
    return <DrugIndexPage drugs={data.drugs} />;
  }

  if (data.type === "drug-detail" && data.drug) {
    return <DrugDetailPage drug={data.drug} />;
  }

  return null;
}
